import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { B3SelectItem } from 'src/app/shared/models/b3-select-item.model';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { CarbonModalService } from 'src/app/services/modal/carbon-modal.service';
import { WorkingDaysService } from 'src/app/services/working-days/working-days.service';
import { CarbonOperation } from 'src/app/shared/interfaces/carbon-operation';

import { OperationFilter } from '../shared/operation-filter';
import { OperationListService } from './shared/operation-list.service';

@Component({
  standalone: false,
  selector: 'app-operation-list',
  templateUrl: './operation-list.component.html',
  styleUrls: ['./operation-list.component.scss']
})
export class OperationListComponent implements OnInit, OnDestroy {

  showFilter = false;
  isDepositDrawerOpen = false;
  selectedDepositOperation: CarbonOperation | null = null;
  isRetirementDrawerOpen = false;
  selectedRetirementOperation: CarbonOperation | null = null;
  isTransferDrawerOpen = false;
  selectedTransferOperation: CarbonOperation | null = null;
  isCustodyWithdrawalDrawerOpen = false;
  selectedCustodyWithdrawalOperation: CarbonOperation | null = null;
  isAcxTradeDrawerOpen = false;
  selectedAcxTradeOperation: CarbonOperation | null = null;
  isB3WithdrawalTransferDrawerOpen = false;
  selectedB3WithdrawalTransferOperation: CarbonOperation | null = null;
  today: Date = new Date();
  workingDays: string[] = [];
  selectItens: B3SelectItem[] = [];
  selectedItem: B3SelectItem = { label: '' };
  activePeriodIndex = 1;
  valueTotal = 0;
  filtersTotal = 0;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly carbonModalService: CarbonModalService,
    private readonly operationListService: OperationListService,
    private readonly workingDaysService: WorkingDaysService,
    private readonly datePipe: DatePipe,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.initDateSubscription();
    this.getWorkingDays();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get periodOptions(): B3SelectItem[] {
    return this.selectItens;
  }

  private initDateSubscription(): void {
    this.operationListService.isSetDate
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(isSet => {
        if (!isSet) {
          return;
        }

        this.activatePersonalizedPeriod();
        this.operationListService.isSetDate.next(false);
      });
  }

  onPeriodToggleChange(event: Event): void {
    const customEvent = event as CustomEvent<{ index: number; id: string | null }>;
    const index = customEvent.detail?.index;

    if (index === undefined || index === null) {
      return;
    }

    const option = this.periodOptions[index];

    if (!option) {
      return;
    }

    if (option.label === 'Personalizado') {
      this.activatePersonalizedPeriod();
      this.openFilter();
      return;
    }

    this.activePeriodIndex = index;
    this.selectedItem = option;
    this.onChangeDate();
  }

  private activatePersonalizedPeriod(): void {
    const personalizedIndex = this.selectItens.findIndex(item => item.label === 'Personalizado');

    if (personalizedIndex < 0) {
      return;
    }

    this.activePeriodIndex = personalizedIndex;
    this.selectedItem = this.selectItens[personalizedIndex];
  }

  trackByPeriodLabel(_index: number, option: B3SelectItem): string {
    return option.label ?? `${_index}`;
  }

  onChangeDate(): void {
    const date = new Date(this.selectedItem.data ?? '');
    date.setDate(date.getDate() + 1);
    this.find(date);
  }

  getWorkingDays(): void {
    this.workingDaysService.getWorkingDays(this.today, 60)
      .subscribe(response => {
        this.workingDays = response;
        const today = this.datePipe.transform(this.today, 'yyyy-MM-dd') ?? '';

        this.selectItens = [
          { label: 'Hoje', data: today },
          { label: 'Últimos 15 dias', data: this.workingDays[14] },
          { label: 'Últimos 30 dias', data: this.workingDays[29] },
          { label: 'Últimos 60 dias', data: this.workingDays[59] },
          { label: 'Personalizado', data: undefined },
        ];

        this.selectedItem = this.selectItens[1] ?? this.selectItens[0];
        this.activePeriodIndex = 1;
        this.onChangeDate();
      });
  }

  openDetail(carbonOperation: CarbonOperation): void {
    this.carbonModalService.openOperationDetailModal(carbonOperation);
  }

  find(date: Date): void {
    const filterDate = this.datePipe.transform(date, 'yyyy-MM-dd') || '';
    const finalDate = this.selectItens[0]?.data ?? filterDate;

    if (this.selectedItem.label !== 'Personalizado') {
      this.operationListService.patchFilters({
        initialDate: filterDate,
        finalDate,
      });
    }
  }

  clearFilters(): void {
    this.operationListService.patchFilters({
      holderCode: '',
      purchaserCode: '',
      carbonCreditCode: '',
      operationCode: '',
      typeCode: undefined,
    });
  }

  openFilter(): void {
    this.activatePersonalizedPeriod();
    this.showFilter = true;
  }

  openDepositDetail(operation: CarbonOperation): void {
    this.selectedDepositOperation = operation;
    this.isDepositDrawerOpen = true;
  }

  onDepositDrawerOpenChange(isOpen: boolean): void {
    this.isDepositDrawerOpen = isOpen;

    if (!isOpen) {
      this.selectedDepositOperation = null;
    }
  }

  openRetirementDetail(operation: CarbonOperation): void {
    this.selectedRetirementOperation = operation;
    this.isRetirementDrawerOpen = true;
  }

  onRetirementDrawerOpenChange(isOpen: boolean): void {
    this.isRetirementDrawerOpen = isOpen;

    if (!isOpen) {
      this.selectedRetirementOperation = null;
    }
  }

  openTransferDetail(operation: CarbonOperation): void {
    this.selectedTransferOperation = operation;
    this.isTransferDrawerOpen = true;
  }

  onTransferDrawerOpenChange(isOpen: boolean): void {
    this.isTransferDrawerOpen = isOpen;

    if (!isOpen) {
      this.selectedTransferOperation = null;
    }
  }

  openCustodyWithdrawalDetail(operation: CarbonOperation): void {
    this.selectedCustodyWithdrawalOperation = operation;
    this.isCustodyWithdrawalDrawerOpen = true;
  }

  onCustodyWithdrawalDrawerOpenChange(isOpen: boolean): void {
    this.isCustodyWithdrawalDrawerOpen = isOpen;

    if (!isOpen) {
      this.selectedCustodyWithdrawalOperation = null;
    }
  }

  openAcxTradeDetail(operation: CarbonOperation): void {
    this.selectedAcxTradeOperation = operation;
    this.isAcxTradeDrawerOpen = true;
  }

  onAcxTradeDrawerOpenChange(isOpen: boolean): void {
    this.isAcxTradeDrawerOpen = isOpen;

    if (!isOpen) {
      this.selectedAcxTradeOperation = null;
    }
  }

  openB3WithdrawalTransferDetail(_operation: CarbonOperation): void {
    this.selectedB3WithdrawalTransferOperation = _operation;
    this.isB3WithdrawalTransferDrawerOpen = true;
  }

  onB3WithdrawalTransferDrawerOpenChange(isOpen: boolean): void {
    this.isB3WithdrawalTransferDrawerOpen = isOpen;

    if (!isOpen) {
      this.selectedB3WithdrawalTransferOperation = null;
    }
  }

  onValueTotal(value: number): void {
    this.valueTotal = value;
  }

  onValueFilterTotal(value: number): void {
    this.filtersTotal = value;
    this.cdr.markForCheck();
  }
}
